import { useCallback } from "react";
import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { Enduser, FormResponseValue, FormResponseValueAnswer } from "@tellescope/types-models";
import {
  convertFormFieldType,
  convertStoredResponseToLocal,
  deriveComputedValueKey,
  resolvePrepopulatedValue,
} from "../../utils";
import { TellescopeFieldResponse } from "../../types";
import { useSeedEmptyResponse } from "./use-seed-empty-response";
import type { LocalTellescopeFormResponseMap } from "./use-tellescope-form-core";

export interface UseResolveCurrentResponsesArgs {
  publicIdentifier: string | undefined;
}

export interface ResolveCurrentResponsesArgs {
  fieldsArr: TellescopeModelFromName<"form_fields">[] | undefined;
  unsavedResponses: LocalTellescopeFormResponseMap;
  responsesMap: Record<string, FormResponseValue> | undefined;
  existingResponses: FormResponseValue[] | undefined;
  enduserForPrepopulation: TellescopeModelFromName<"endusers"> | Enduser | undefined;
}

const hydrateStoredResponse = (
  stored: FormResponseValue,
  field: TellescopeModelFromName<"form_fields">
): TellescopeFieldResponse => {
  const hydrated = convertStoredResponseToLocal(stored);
  return {
    ...hydrated,
    computedValueKey: hydrated.computedValueKey ?? deriveComputedValueKey(field.intakeField, hydrated.answer),
  };
};

/**
 * Returns a resolver for the effective answer of every field, in precedence order: an in-progress
 * edit wins over a saved response, which wins over a response passed in from a previous submission,
 * which wins over a value prepopulated from the enduser record, which wins over the field's seeded default.
 */
export const useResolveCurrentResponses = ({ publicIdentifier }: UseResolveCurrentResponsesArgs) => {
  const seedEmptyResponse = useSeedEmptyResponse({ publicIdentifier });

  return useCallback(
    ({
      fieldsArr,
      unsavedResponses,
      responsesMap,
      existingResponses,
      enduserForPrepopulation,
    }: ResolveCurrentResponsesArgs): LocalTellescopeFormResponseMap =>
      fieldsArr?.reduce<LocalTellescopeFormResponseMap>((acc, field) => {
        const unsavedResponse = unsavedResponses[field.id];
        if (unsavedResponse) {
          acc[field.id] = unsavedResponse;
          return acc;
        }

        const savedResponse = responsesMap?.[field.id];
        if (savedResponse) {
          if (convertFormFieldType(savedResponse.answer.type) === null) return acc;
          acc[field.id] = hydrateStoredResponse(savedResponse, field);
          return acc;
        }

        if (existingResponses) {
          const existing = existingResponses.find((r) => r.fieldId === field.id);
          if (existing) {
            if (convertFormFieldType(existing.answer.type) !== null) {
              acc[field.id] = hydrateStoredResponse(existing, field);
              return acc;
            }
          }
        }

        if (convertFormFieldType(field.type) === null) return acc;

        if (field.prepopulateFromFields) {
          const prepopulated = resolvePrepopulatedValue(field, enduserForPrepopulation);
          if (prepopulated !== undefined) {
            // @ts-expect-error Union type gets in the way here
            const answer: FormResponseValueAnswer = { type: field.type, value: prepopulated };
            acc[field.id] = {
              fieldId: field.id,
              fieldTitle: field.title,
              answer,
              computedValueKey: deriveComputedValueKey(field.intakeField, answer),
              ...(field.disabledWhenPrepopulated ? { disabled: true } : {}),
            };
            return acc;
          }
        }

        const seeded = seedEmptyResponse(field);
        if (seeded) acc[field.id] = seeded;
        return acc;
      }, {}) ?? {},
    [seedEmptyResponse]
  );
};
